<template>
  <DeleteConfirmDialog
    v-model:visible="deleteDialogVisible"
    :title="i18n.t('deleteGroup')"
    :sub-title="i18n.t('deleteGroupText', { name: accountGroup?.name })"
    @delete="handleDeleteConfirm"
    @cancel="deleteDialogVisible = false" />

  <SmDialog
    v-model:visibility="visibility"
    :title="mode == 'add' ? i18n.t('addGroup') : i18n.t('editGroup')"
    :size="dialogSizes[currentStep]">
    <!-- Close button -->
    <template v-if="mode == 'edit'" #close>
      <div style="display: flex; gap: 0.6rem; align-items: center">
        <Trash
          :size="20"
          class="cursor-pointer hover:text-magenta"
          @click="handleDelete" />
        <SmButtonClose @close="visibility = false" />
      </div>
    </template>

    <!-- Meta -->
    <GroupAddForm
      v-if="currentStep === 'form'"
      ref="groupAddFormRef"
      v-model:account-group="accountGroup"
      @next="next" />

    <!-- Software and License -->
    <GroupAddSoftware
      v-if="'license' === currentStep"
      ref="groupAddSoftwareRef"
      v-model:account-group="accountGroup"
      v-model:selection="applicationSelection"
      v-model:step="currentStep"
      :all-software-licenses="allSoftwareLicenses"
      :all-contracts="allContracts" />

    <!-- Footer -->
    <template #footer>
      <div class="dialog-footer">
        <div>
          <div
            v-if="currentStep !== 'form'"
            class="dialog-footer-left footer-button"
            @click="prev">
            <ChevronLeft :size="20" />

            <div class="progress-button-text">
              {{ i18n.t('back') }}
            </div>
          </div>
        </div>

        <div class="dialog-footer-right footer-button" @click="next">
          <div class="progress-button-text">
            {{ i18n.t(currentStep === 'license' ? 'save' : 'next') }}
          </div>
          <ChevronRight :size="20" />
        </div>
      </div>
    </template>
  </SmDialog>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import { SelectionState } from '@/components/groups/GroupAddSoftware.vue'

  import {
    saasmetrix__models__account__AccountGroup__AccountGroup as AccountGroup,
    AccountGroupIn,
    AccountGroupUpdateIn,
    PriceInformation,
    SoftwareLicense,
    SoftwareOut,
    SoftwarePermissionUpdate,
    SoftwarePermission_Output,
    UserGroupsService,
  } from '@/client'
  import { DialogSize } from '@/components/sm/SmDialog.vue'
  import type GroupAddForm from './GroupAddForm.vue'
  import GroupAddSoftware from './GroupAddSoftware.vue'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { ChevronLeft, ChevronRight, Trash } from 'lucide-vue-next'

  const i18n = useI18n()
  const emit = defineEmits(['groupsChanged'])
  const groupAddFormRef = ref<InstanceType<typeof GroupAddForm> | null>(null)
  const groupAddSoftwareRef = ref<InstanceType<typeof GroupAddSoftware> | null>(
    null
  )
  // TODO: Enable this when group sync feature is fully implemented
  const groupSyncActive = ref(false)

  const visibility = defineModel<boolean>('visibility')

  const props = defineProps({
    allContracts: {
      type: Array as PropType<PriceInformation[]>,
      required: true,
    },
    allSoftwareLicenses: {
      type: Array as PropType<SoftwareLicense[]>,
      required: true,
    },
    mode: {
      type: String as PropType<'add' | 'edit'>,
      default: 'add',
    },
  })

  const accountGroup = defineModel('accountGroup', {
    type: Object as () => AccountGroupUpdateIn | AccountGroup | AccountGroupIn,
    default: () => ({
      name: '',
      notes: '',
      color: '',
      config: {},
      accounts: [],
      permission: {
        software_permissions: {},
      },
      data_source: {
        data_source_id: '',
        external_id: '',
      },
    }),
  })

  //   Steps
  const steps = ['form', 'license'] as const
  type Steps = (typeof steps)[number]

  const dialogSizes: Record<Steps, DialogSize> = {
    form: 'medium',
    license: 'large',
  } as const

  const currentStep: Ref<Steps> = ref('form')
  const applicationSelection = ref<SelectionState>({})

  function getCurrentStep() {
    return steps.indexOf(currentStep.value)
  }

  function checkSyncGroupSelection() {
    return Object.keys(applicationSelection.value).some(
      (appId) => applicationSelection.value[appId] !== null
    )
  }

  async function handleGroupSaveRequest(requestPromise: Promise<any>) {
    try {
      await requestPromise
      applicationSelection.value = {}
      visibility.value = false
    } catch (err) {
      console.error(err)
    } finally {
      emit('groupsChanged')
    }
  }

  function saveGroup() {
    updateGroupPermissions()

    handleGroupSaveRequest(
      UserGroupsService.postAccountGroupApiV1ManagementGroupsPost({
        requestBody: accountGroup.value as AccountGroupIn,
      })
    )
  }

  function updateAccountGroup() {
    updateGroupPermissions()

    if (!('_id' in accountGroup.value))
      return console.error('Try to update a group without an ID')

    handleGroupSaveRequest(
      UserGroupsService.patchAccountGroupApiV1ManagementGroupsAccountGroupIdPatch(
        {
          accountGroupId: (accountGroup.value as AccountGroup)._id,
          requestBody: accountGroup.value,
        }
      )
    )
  }

  function hasDataSource(
    obj: AccountGroupUpdateIn | AccountGroup | AccountGroupIn
  ): obj is AccountGroup | AccountGroupIn {
    return 'data_source' in obj
  }

  function next() {
    const isSyncGroupSelectionInvalid =
      groupSyncActive.value && checkSyncGroupSelection() === false
    const isAccountGroupNameInvalid =
      !accountGroup.value?.name || accountGroup.value.name.trim() === ''
    const isDataSourceInvalid =
      groupSyncActive.value &&
      hasDataSource(accountGroup.value) &&
      (!accountGroup.value.data_source?.data_source_id ||
        !accountGroup.value.data_source?.external_id)

    if (
      isSyncGroupSelectionInvalid ||
      isAccountGroupNameInvalid ||
      isDataSourceInvalid
    ) {
      return groupAddFormRef.value?.formRef?.validate()
    }

    // Validate that at least one software/license is selected before saving
    if (currentStep.value === 'license') {
      // Directly save when on license step (no finish step anymore)
      if (props.mode === 'add') {
        saveGroup()
      } else {
        updateAccountGroup()
      }
    } else {
      // Navigate to next step
      const currentStepIndex = getCurrentStep()
      if (currentStepIndex < steps.length - 1) {
        currentStep.value = steps[currentStepIndex + 1]
      }
    }
  }

  function prev() {
    const currentStepIndex = getCurrentStep()

    if (currentStepIndex > 0) {
      currentStep.value = steps[currentStepIndex - 1]
    }
  }

  // #region Delete
  const deleteDialogVisible = ref(false)
  function handleDelete() {
    deleteDialogVisible.value = true
  }

  function handleDeleteConfirm() {
    if (!('_id' in accountGroup.value))
      return console.error('Try to delete a group without an ID')

    UserGroupsService.deleteAccountGroupApiV1ManagementGroupsAccountGroupIdDelete(
      {
        accountGroupId: accountGroup.value._id,
      }
    )
      .then(() => {
        visibility.value = false
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => {
        emit('groupsChanged')
        deleteDialogVisible.value = false
      })
  }
  // #endregion

  watch(
    visibility,
    () => {
      currentStep.value = 'form'
    },
    { immediate: true }
  )

  function updateGroupPermissions() {
    const _applicationPermission = convertSelectionToSoftwarePermission(
      applicationSelection.value
    )
    if (!accountGroup.value.permission) {
      accountGroup.value.permission = {
        software_permissions: {},
      }
    }
    accountGroup.value.permission.software_permissions = _applicationPermission
  }

  const { data: allApplications } =
    ApplicationStore.useApplications<SoftwareOut>()
  function convertSelectionToSoftwarePermission(selection: SelectionState) {
    const _accountGroupPermissions: Record<
      string,
      SoftwarePermissionUpdate | null
    > = {}

    for (const [applicationId, subAccountEntries] of Object.entries(
      selection
    )) {
      if (subAccountEntries == null) {
        _accountGroupPermissions[applicationId] = null
        continue
      }

      let hasAnyLicense = false
      for (const licenses of Object.values(subAccountEntries)) {
        if (licenses && Object.values(licenses).some((l) => l !== null)) {
          hasAnyLicense = true
          break
        }
      }

      _accountGroupPermissions[applicationId] = {
        sub_account_permissions: {},
      }
      // No licenses chosen -> set dummy value for the patch request to work
      if (!hasAnyLicense) {
        const app = allApplications.value.find((a) => a._id == applicationId)
        if (!app) continue // should not happen
        const dummySubAccountId = Object.keys(app.sub_accounts)[0]
        _accountGroupPermissions[applicationId]!.sub_account_permissions![
          dummySubAccountId
        ] = {
          auto_assign: true,
          auto_unassign: true,
          software_license_permissions: {},
        }
        continue
      }

      for (const [subAccountId, softwareLicenseEntries] of Object.entries(
        subAccountEntries
      )) {
        // Skip if no licenses in this subaccount (or all null)
        const validLicenses = Object.entries(softwareLicenseEntries).filter(
          ([, l]) => l !== null
        )

        if (validLicenses.length === 0) continue

        _accountGroupPermissions[applicationId]!.sub_account_permissions![
          subAccountId
        ] = {
          auto_assign: true,
          auto_unassign: true,
          software_license_permissions: {},
        }

        for (const [softwareLicenseId] of validLicenses) {
          _accountGroupPermissions[applicationId]!.sub_account_permissions![
            subAccountId
          ]!.software_license_permissions![softwareLicenseId] = {
            auto_assign: true,
            auto_unassign: true,
          }
        }
      }
    }
    return _accountGroupPermissions
  }

  watch(
    accountGroup,
    () => {
      if (!accountGroup.value.permission?.software_permissions) return

      applicationSelection.value = convertApplicationPermissionToSelection(
        (accountGroup.value as AccountGroup).permission.software_permissions
      )
    },
    { immediate: true }
  )

  function convertApplicationPermissionToSelection(
    applicationPermission: Record<string, SoftwarePermission_Output>
  ) {
    const _selection: SelectionState = {}

    for (const [applicationId, softwareEntry] of Object.entries(
      applicationPermission
    )) {
      _selection[applicationId] = {}
      for (const [subAccountId, subAccountEntry] of Object.entries(
        softwareEntry.sub_account_permissions || {}
      )) {
        _selection[applicationId]![subAccountId] = {}
        for (const [softwareLicenseId] of Object.entries(
          subAccountEntry.software_license_permissions || {}
        )) {
          const softwareLicense = props.allSoftwareLicenses.find(
            (l) => l._id == softwareLicenseId
          )
          if (softwareLicense)
            _selection[applicationId]![subAccountId][softwareLicenseId] = {
              ...softwareLicense,
            }
        }
      }
    }
    return _selection
  }

  function reset() {
    accountGroup.value = {
      name: '',
      accounts: [],
      permission: {
        software_permissions: {},
      },
      color: {
        r: 0,
        g: 0,
        b: 0,
      },
    }
    applicationSelection.value = {}
    currentStep.value = 'form'
  }

  watch(visibility, () => {
    if (!visibility.value) {
      reset()
    }
  })
</script>

<style scoped lang="scss">
  .dialog-footer {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }

  .dialog-footer-left {
    display: flex;
    align-items: center;
  }

  .dialog-footer-right {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  .footer-button {
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    transition: all 0.2s ease-in-out;

    &-next {
      right: 20px;
    }

    &-prev {
      left: 0px;
    }

    &:hover {
      scale: 1.05;
    }

    svg {
      fill: var(--sm-contrast);
    }
  }
</style>
